import {useEffect, useMemo, useState} from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Body} from 'shells/typography/body';

import {authToService} from '../../integrations/auth-to-service';
import {getAuthUrl} from '../../integrations/integration-mutation-response-helpers';

import {GOOGLE_WINDOW_FEATURES, OFFICE365_WINDOW_FEATURES} from '../consts';
import {
    useGetCalendarSync,
    useSelectCalendarSyncCalendar,
    useSetGoogleCalendarSyncIsEnabled,
    useSetOffice365CalendarSyncIsEnabled,
} from '../graphql/hooks';
import {CalendarList} from '../select-calendar-modal/calendar-list';
import {getSelectedCalendarId} from '../select-calendar-modal/helpers';

import {CalendarModalPage} from './calendar-modal-page';

type Props = {
    nextPage: () => void;
};

export function CalendarSelect(props: Props) {
    const [selectedCalendarId, setSelectedCalendarId] = useState<string | null | undefined>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null | undefined>(null);

    const {calendarSync, isLoading, refetch: refetchCalendarSync} = useGetCalendarSync();

    const {selectCalendarSyncCalendar} = useSelectCalendarSyncCalendar();
    const {enableGoogle} = useSetGoogleCalendarSyncIsEnabled();
    const {enableO365} = useSetOffice365CalendarSyncIsEnabled();

    const calendars = useMemo(() => (calendarSync ? calendarSync.calendars : []), [calendarSync]);

    useEffect(() => {
        if (calendars.length > 0) {
            const preselected = getSelectedCalendarId(calendars, 'sync');
            if (preselected) {
                setSelectedCalendarId(preselected);
            } else if (calendars[0]) {
                setSelectedCalendarId(calendars[0].id);
            }
        } else {
            setSelectedCalendarId(null);
        }
    }, [calendars]);

    const handleSave = () => {
        if (!selectedCalendarId) {
            return;
        }

        setIsSaving(true);
        selectCalendarSyncCalendar(selectedCalendarId)
            .then(() => {
                props.nextPage();
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    const handleReauth = () => {
        if (!calendarSync) {
            return;
        }

        setErrorMessage(null);

        const isGoogle =
            calendarSync.externalService === 'GOOGLE' ||
            calendarSync.externalService === 'GOOGLE_APPS';

        const enableMutation = isGoogle ? enableGoogle : enableO365;
        const authCheckUrl = isGoogle ? '/google/has-auth' : '/office365/has-auth';
        const windowFeatures = isGoogle ? GOOGLE_WINDOW_FEATURES : OFFICE365_WINDOW_FEATURES;

        enableMutation({variables: {isEnabled: true}})
            .then(getAuthUrl)
            .then((authUrl) => {
                if (authUrl) {
                    return authToService({
                        authUrl,
                        windowFeatures,
                        authCheckUrl,
                    });
                }

                return {hasAuth: true};
            })
            .then(() => {
                refetchCalendarSync();
            })
            .catch((error) => {
                const errMsg = Array.isArray(error)
                    ? error.join(', ')
                    : 'Failed to authenticate. Please try again.';
                setErrorMessage(errMsg);
            });
    };

    const serviceDisplayName =
        calendarSync && calendarSync.externalService === 'OFFICE365' ? 'Office 365' : 'Google';

    let content = null;
    if (isLoading) {
        content = <LoadingPage fullHeight={false} />;
    } else if (calendars.length > 0) {
        content = (
            <CalendarList
                menuMaxHeight={300}
                calendars={calendars}
                selectedCalendarId={selectedCalendarId}
                onSelectCalendar={setSelectedCalendarId}
                modalType='sync'
            />
        );
    } else {
        content = (
            <Stack spacing={12}>
                <Body size='large'>No calendars found for your {serviceDisplayName} account.</Body>
                <div className='half-width'>
                    <Button
                        variant='primary'
                        size='big'
                        onClick={handleReauth}
                        minWidthFitContent={true}
                    >
                        Authenticate to fetch calendars
                    </Button>
                </div>
                {errorMessage && <Body color='error'>{errorMessage}</Body>}
            </Stack>
        );
    }

    return (
        <CalendarModalPage
            header='Choose a calendar to sync'
            subheader='Nutshell can add events to this calendar and import events from it'
            navButtons={
                <SaveButton
                    isSaving={isSaving}
                    disabled={!selectedCalendarId}
                    // @ts-expect-error
                    onClick={handleSave}
                    buttonText='Continue'
                />
            }
        >
            {content}
        </CalendarModalPage>
    );
}
